// packages/engine/src/process-trace-producer.ts
//
// P1b — per-construct outcome selection. The emitter (process-step-emitter.ts)
// selects only if/try and emits the static superset for every other construct;
// this module implements outcome selection ITSELF over the IR. Given a resolved
// `Selection` (which select cases run + order, which parallel branches complete,
// a retry attempt outcome, a loop count), `produceTrace` walks the IR directly
// and materialises the corresponding linear `Trace` — the net-new core.
//
// It is a THIN selection-aware DFS: all leaf/modifier/desugar logic is shared
// with the render walker via process-step-build.ts + process-ir-desugar.ts, so a
// produced trace is leaf-for-leaf byte-identical to `walkProcess` under the
// equivalent selection (the sync-equality anchor). Only the DISPATCH — pruning,
// ordering, and multiplicity — is new here.
//
// Scope boundary (design §): P1b produces the STRUCTURAL outcome; P3 adds injected
// failure as exception propagation over the same walk (`Selection.failures` — a
// step `fails` routes to a matching `catch`, drives a retry's per-attempt unroll,
// fires a `reversible`'s completed-item compensations in reverse, or falls through
// to a process fail). Race-winner/parallel-completion derivation from virtual-clock
// timings (P4), extended-pin parsing (P5), and loop/go bounding + OPEN cap (P1c)
// resolve their inputs upstream; `go` is emitted as the walker-identical single
// marker + reentry edge, with count-driven unrolling in P1c.

import type {
  ProcessModel,
  SubprocessModel,
  Flow,
  ProcessNode,
  CallNode,
  SelectNode,
  SelectCount,
  BranchNode,
  GuardNode,
  RetryNode,
  LoopNode,
  ParallelNode,
  ReversibleNode,
  SpliceNode,
  OwnerBlockNode,
  TerminatorNode,
  CatchClause,
  UnwindAction,
} from "./process-model.js";
import type {
  NodeId,
  Reentry,
  StepModifiers,
  WalkStep,
  WalkNode,
  WalkerContext,
} from "./process-walker-types.js";
import type { EmittedStep, EmitContainer, EmitGroup } from "./process-step-emitter.js";
import type { Reason } from "./process-latency.js";
import {
  DEFAULT_MODS,
  applyDist,
  groupDelta,
  buildLeafStep,
  resolveReentries,
} from "./process-step-build.js";
import { demoteMultiAwaitHead, awaitClauseTimerFlow } from "./process-ir-desugar.js";
import { substituteTemplateCallerIR } from "./process-ir-walk.js";

/** The terminator flavour surfaced in a trace's event stream — the render
 *  walker's own terminator {@link WalkNode}, so P2/P3 read one end model. */
export type TerminatorEvent = Extract<WalkNode, { kind: "terminator" }>;

/** One event in a trace, in execution order: an index-consuming step or a
 *  terminator. `steps` (below) is the WalkStep-only projection. */
export type TraceEvent = WalkStep | TerminatorEvent;

/** A parallel's structural outcome — which branches' steps appear. `all` = every
 *  branch (AND-join / bare); `complete` = the given branch indices completed
 *  (join N / boolean merge — WHICH set is derived by P4 for a race, passed in as
 *  a size-1 set). Branch index `body.branches.length` is the desugared
 *  awaitClause timer branch. */
export type ParallelOutcome =
  | { readonly kind: "all" }
  | {
      readonly kind: "complete";
      readonly branches: readonly number[];
      /** P5 — a boolean/count merge that did NOT reach threshold over the pinned
       *  done-set: the completed branches' steps still appear, then the parallel
       *  fails the merge (the `join (fraud and credit)` couldn't proceed). Absent
       *  ⇒ the merge is satisfied and the flow continues past the parallel. */
      readonly mergeFail?: { readonly reason?: string };
    };

/** A retry's structural outcome when NO failure is injected. `succeed` = attempt
 *  body once; `exhaust` = attempt + one catch (default first) + `elseBody`, once.
 *  A `Selection.failures` hit on an attempt step drives the per-attempt unroll
 *  instead (P3). */
export type RetryOutcome =
  | { readonly kind: "succeed" }
  | { readonly kind: "exhaust"; readonly catchIndex?: number };

/** One injected outcome for an occurrence of a failure-pinned step (P3). `fail`
 *  throws (optionally carrying a `cause` routed to a matching `catch` label);
 *  `ok` completes normally. A single `fails "c"` is `[{fail,"c"}]`; a per-attempt
 *  `fails "c", ok` sequence is `[{fail,"c"},{ok}]`. */
export type FailureOutcome =
  | { readonly kind: "fail"; readonly cause?: string }
  | { readonly kind: "ok" };

/** The ordered per-occurrence (per retry-attempt / per emission) outcome list for
 *  one failure-pinned step. */
export type FailureSpec = readonly FailureOutcome[];

/** Per-construct outcome descriptors, keyed by the emitter nodeId (the same
 *  `p:<id>/…` scheme the P1a choice bridge resolves pins to). Every field is
 *  optional; a missing entry takes the construct's structural default. */
export interface Selection {
  /** if/try arm pick — container nodeId → chosen arm label (from P1a). */
  readonly branchSelection?: ReadonlyMap<NodeId, string>;
  /** select container nodeId → ordered case indices to run. */
  readonly selectRuns?: ReadonlyMap<NodeId, readonly number[]>;
  /** loop / parallel-fanOut container nodeId → iteration count (default 1). */
  readonly loopCounts?: ReadonlyMap<NodeId, number>;
  /** go nodeId → back-iteration count. Plumbed for P1c; not consumed in P1b. */
  readonly goCounts?: ReadonlyMap<NodeId, number>;
  /** parallel container nodeId → which branches complete. */
  readonly parallelOutcomes?: ReadonlyMap<NodeId, ParallelOutcome>;
  /** retry container nodeId → attempt outcome. */
  readonly retryOutcomes?: ReadonlyMap<NodeId, RetryOutcome>;
  /** P3 — injected failures keyed by the IR action node (a call/await/note); the
   *  enumerator resolves `fails`/failure-sequence pins to these. Each emitted
   *  occurrence of the node consumes the next {@link FailureOutcome} in order. */
  readonly failures?: ReadonlyMap<ProcessNode, FailureSpec>;
}

/** A choice point the producer left at its default (P1c widens these into OPEN
 *  bounds; P1b records the reason + nodeId only). */
export interface OpenReason {
  readonly reason: Reason;
  readonly nodeId: NodeId;
}

/** One materialised trace: the ordered event stream, the WalkStep projection
 *  (occurrence / order / count basis), the terminal outcome if any, and the
 *  OPEN dimensions that made it partial. */
export interface Trace {
  readonly events: readonly TraceEvent[];
  readonly steps: readonly WalkStep[];
  /** The IR node behind each `steps[i]` (a `WalkStep` carries none) — P4's
   *  time-budget path rebuilds a pruned `Flow` from these to re-fold via
   *  `estimateProcess`. A chained call repeats its one `CallNode` per hop. */
  readonly stepNodes: readonly ProcessNode[];
  readonly end?: { readonly type: "fail" | "finish"; readonly reason?: string; readonly all?: boolean };
  readonly open: readonly OpenReason[];
}

/** How far a hit terminator truncates: `branch` = up to the nearest parallel-
 *  branch boundary (or the process root when not inside one); `root` = the whole
 *  process (`fail all` / `finish all`, §2.4). */
type Terminated = null | "branch" | "root";

/** The default run-set for an unpinned select (PROCESS §3): bare = every case in
 *  order; `one` = the first; `N` = the first N. */
function defaultSelectRun(count: SelectCount, len: number): number[] {
  const n = count.kind === "all" ? len : count.kind === "one" ? 1 : count.n;
  return Array.from({ length: Math.max(0, Math.min(n, len)) }, (_, i) => i);
}

/**
 * Materialise the linear trace for one fully-resolved outcome selection. Walks
 * the process IR directly, threading StepModifiers through {@link groupDelta}
 * and building leaves through {@link buildLeafStep} (both shared with the render
 * walker), so leaf steps are byte-identical to `walkProcess` under the same
 * selection. The `stepIndex` counter is TRACE-LOCAL (dense over the linearised
 * trace, fresh per unrolled occurrence) — NOT the renderer's persisted index.
 */
export function produceTrace(process: ProcessModel, selection: Selection, ctx: WalkerContext): Trace {
  const subModels: ReadonlyMap<string, Pick<SubprocessModel, "body"> & Partial<Pick<SubprocessModel, "templateParam">>> =
    ctx.processByName
      ? new Map<string, SubprocessModel | ProcessModel>([...ctx.processByName, ...ctx.subprocessByName])
      : ctx.subprocessByName;

  const steps: WalkStep[] = [];
  const stepNodes: ProcessNode[] = [];
  const events: TraceEvent[] = [];
  const asByName = new Map<string, WalkStep>();
  const reentries: Reentry[] = [];
  const open: OpenReason[] = [];
  let counter = 0;
  let terminated: Terminated = null;
  let parallelDepth = 0;
  let end: Trace["end"];
  // P3 — an in-flight injected failure propagating toward the nearest handler
  // (guard `catch` / retry / reversible compensation / process fail). Like
  // `terminated` it truncates the forward continuation; unlike it, a handler can
  // clear it (routing to a catch / else) so the flow continues.
  let thrown: { readonly cause?: string } | null = null;
  const failures: ReadonlyMap<ProcessNode, FailureSpec> = selection.failures ?? new Map();
  // Per-node occurrence index — a retry re-runs the same attempt `Flow`, so the
  // same IR node recurs; occurrence order == attempt order (attempt-indexing).
  const occ = new Map<ProcessNode, number>();

  const rootId = `p:${process.id ?? process.name}`;

  // A loop/`go` count is keyed by the loop container's STATIC nodeId; an inner
  // loop reached inside an outer iteration >0 carries an occurrence marker
  // (`…/body#2/loop:0`), so strip the markers before the count lookup.
  const staticId = (id: NodeId): NodeId => id.replace(/#\d+/g, "");

  // Synthesize the minimal EmitContainer/EmitGroup groupDelta reads, so the
  // producer's modifiers are computed by the SAME function the walker uses.
  const delta = (
    parent: StepModifiers,
    node: ProcessNode,
    irKind: ProcessNode["kind"],
    nodeId: NodeId,
    role: EmitGroup["role"],
    opts?: { label?: string; selected?: boolean; expanded?: boolean },
  ): StepModifiers => {
    const c = { irKind, nodeId, node, startIndex: 0, expanded: opts?.expanded } as EmitContainer;
    const g = { role, nodeId: `${nodeId}/${role}`, label: opts?.label, selected: opts?.selected ?? true } as EmitGroup;
    return groupDelta(parent, c, g);
  };

  const emitLeaf = (
    kind: EmittedStep["kind"],
    node: ProcessNode,
    nodeId: NodeId,
    mods: StepModifiers,
    chain: readonly string[],
    hop?: EmittedStep["hop"],
    hopIndex?: number,
    isLastHop?: boolean,
  ): void => {
    const u = { kind, stepIndex: counter++, nodeId, node, hop, hopIndex, isLastHop, chain } as EmittedStep;
    const ws = buildLeafStep(u, mods, asByName, reentries);
    steps.push(ws);
    stepNodes.push(node);
    events.push(ws);
    if (kind === "call" && isLastHop) {
      const an = (node as CallNode).asName?.name;
      if (an && !asByName.has(an)) asByName.set(an, ws);
    }
    // P3 — the step is emitted (it was attempted); a failure injected on it fires
    // AFTER it appears. A multi-hop call fails at its last hop (the callee).
    if (kind === "call" && !isLastHop) return;
    const spec = failures.get(node);
    if (spec) {
      const n = occ.get(node) ?? 0;
      occ.set(node, n + 1);
      const o = spec[n];
      if (o?.kind === "fail") thrown = o.cause != null ? { cause: o.cause } : {};
    }
  };

  const runFlow = (flow: Flow | undefined, parentId: string, mods: StepModifiers, chain: readonly string[]): void => {
    let i = 0;
    for (const n of flow ?? []) {
      if (terminated || thrown) break;
      visit(n, `${parentId}/${n.kind}:${i}`, mods, chain);
      i++;
    }
  };

  // P3 — emit one item's `unwind` compensation. Runs while `thrown` is set, so it
  // parks `thrown` (the forward-truncation guard) around the emission and restores
  // it afterwards; a call/block/splice re-enters the shared walk (compensation
  // mods carry `compensation:true`), an `unwind fail` terminator ends the chain.
  const emitUnwindAction = (
    action: UnwindAction,
    reversibleId: NodeId,
    unwindId: NodeId,
    m: StepModifiers,
    chain: readonly string[],
  ): void => {
    if (action.kind === "terminator") {
      const t = action;
      events.push({
        kind: "terminator",
        nodeId: `${unwindId}/term`,
        type: t.outcome,
        ...(t.reason ? { reason: t.reason.text } : {}),
        comp: { reversibleId },
      });
      end = { type: t.outcome, ...(t.reason ? { reason: t.reason.text } : {}) };
      terminated = "root";
      thrown = null; // the explicit `unwind fail` is the terminal outcome
      return;
    }
    const saved = thrown;
    thrown = null;
    const body = action.kind === "block" ? action.body : [action as ProcessNode];
    runFlow(body, unwindId, m, chain);
    if (!terminated) thrown = saved;
  };

  function visit(node: ProcessNode, nodeId: NodeId, parentMods: StepModifiers, chain: readonly string[]): void {
    const mods = applyDist(parentMods, node);
    switch (node.kind) {
      case "call": {
        const c = node as CallNode;
        const last = c.hops.length - 1;
        c.hops.forEach((hop, hi) => emitLeaf("call", c, `${nodeId}/hop:${hi}`, mods, chain, hop, hi, hi === last));
        break;
      }
      case "note":
        emitLeaf("note", node, nodeId, mods, chain);
        break;
      case "prose":
        emitLeaf("prose", node, nodeId, mods, chain);
        break;
      case "await":
        emitLeaf("await", node, nodeId, mods, chain);
        break;
      case "reentry":
        // P1b: the walker-identical single `go` marker + reentry edge. Count-
        // driven unrolling (goCounts) is P1c.
        emitLeaf("go", node, nodeId, mods, chain);
        break;
      case "terminator": {
        const t = node as TerminatorNode;
        const ev: TerminatorEvent = {
          kind: "terminator",
          nodeId,
          type: t.outcome,
          ...(t.reason ? { reason: t.reason.text } : {}),
          ...(t.all ? { all: true } : {}),
        };
        events.push(ev);
        if (t.all) {
          end = { type: t.outcome, ...(t.reason ? { reason: t.reason.text } : {}), all: true };
          terminated = "root";
        } else {
          // A bare terminator at the process root ends the run (its outcome IS
          // the process end); inside a parallel branch it ends only that branch.
          if (parallelDepth === 0) end = { type: t.outcome, ...(t.reason ? { reason: t.reason.text } : {}) };
          terminated = "branch";
        }
        break;
      }
      case "branch": {
        const b = node as BranchNode;
        const aspects = b.arms.map((a) => a.condition?.text ?? "");
        const chosen = selection.branchSelection?.get(nodeId) ?? aspects[0] ?? "else";
        const ai = b.arms.findIndex((a) => (a.condition?.text ?? "") === chosen);
        if (ai >= 0) {
          const arm = b.arms[ai]!;
          const m = delta(mods, b, "branch", nodeId, "arm", { label: arm.condition?.text ?? "", selected: true });
          runFlow(arm.body, `${nodeId}/arm:${ai}`, m, chain);
        } else if (b.elseArm && chosen === "else") {
          const m = delta(mods, b, "branch", nodeId, "else", { label: "else", selected: true });
          runFlow(b.elseArm.body, `${nodeId}/else`, m, chain);
        }
        break;
      }
      case "guard": {
        const g = node as GuardNode;
        const chosen = selection.branchSelection?.get(nodeId) ?? "try";
        if (chosen !== "try") {
          // Explicit catch arm-selection (P1a `declined: fails` on the catch label).
          const ci = g.catches.findIndex((c) => (c.label?.text ?? "catch") === chosen);
          if (ci >= 0) {
            const m = delta(mods, g, "guard", nodeId, "catch", { label: chosen, selected: true });
            runFlow(g.catches[ci]!.body, `${nodeId}/catch:${ci}`, m, chain);
          }
          break;
        }
        // Run the guarded work; a failure injected inside it (P3) routes to the
        // catch whose label matches the cause, then the flow continues forward.
        const am = delta(mods, g, "guard", nodeId, "attempt", { label: "try", selected: true });
        runFlow(g.attempt, `${nodeId}/attempt`, am, chain);
        if (thrown) {
          const ci = catchIndexForCause(g.catches, thrown.cause);
          if (ci >= 0) {
            const cause = thrown.cause;
            thrown = null;
            const cm = delta(mods, g, "guard", nodeId, "catch", { label: g.catches[ci]!.label?.text ?? cause ?? "catch", selected: true });
            runFlow(g.catches[ci]!.body, `${nodeId}/catch:${ci}`, cm, chain);
          }
          // No matching catch → `thrown` stays set and propagates outward.
        }
        break;
      }
      case "select": {
        const s = node as SelectNode;
        const runOrder = selection.selectRuns?.get(nodeId) ?? defaultSelectRun(s.count, s.cases.length);
        for (const ci of runOrder) {
          const cs = s.cases[ci];
          if (!cs) continue;
          const m = delta(mods, s, "select", nodeId, "case", { label: cs.label.text, selected: true });
          runFlow(cs.body, `${nodeId}/case:${ci}`, m, chain);
          if (terminated || thrown) break;
        }
        break;
      }
      case "loop": {
        const l = node as LoopNode;
        const count = selection.loopCounts?.get(staticId(nodeId)) ?? 1;
        const m = delta(mods, l, "loop", nodeId, "body");
        for (let k = 0; k < count; k++) {
          // Occurrence-disambiguated body id so repeated iterations don't collide
          // in the `as` registry / reentries.
          const bid = k === 0 ? `${nodeId}/body` : `${nodeId}/body#${k}`;
          runFlow(l.body, bid, m, chain);
          if (terminated || thrown) break;
        }
        break;
      }
      case "retry": {
        const r = node as RetryNode;
        const am = delta(mods, r, "retry", nodeId, "attempt");
        runFlow(r.attempt, `${nodeId}/attempt`, am, chain);
        if (thrown) {
          // P3 — a failure was injected in the attempt. Retry attempt-indexed: on
          // each failure the matching `catch` runs (restoration BETWEEN attempts)
          // and the loop retries; the occurrence counter feeds the next attempt's
          // outcome. On the last attempt the `else` runs (exhaustion).
          const bound = retryBound(r);
          let attempt = 0;
          while (thrown && attempt < bound - 1) {
            const ci = catchIndexForCause(r.catches, thrown.cause);
            thrown = null;
            if (ci >= 0 && r.catches[ci]!.body) {
              const cm = delta(mods, r, "retry", nodeId, "catch");
              runFlow(r.catches[ci]!.body, `${nodeId}/catch:${ci}#${attempt}`, cm, chain);
            }
            attempt++;
            if (terminated) break;
            runFlow(r.attempt, `${nodeId}/attempt#${attempt}`, am, chain);
          }
          if (thrown) {
            // Bound exhausted, still failing: `else` handles it (flow continues),
            // else the failure propagates outward.
            if (r.elseBody) {
              thrown = null;
              const em = delta(mods, r, "retry", nodeId, "elseBody");
              runFlow(r.elseBody, `${nodeId}/elseBody`, em, chain);
            }
          }
          break;
        }
        // No injected failure — structural outcome selection (P1b).
        const outcome = selection.retryOutcomes?.get(nodeId) ?? { kind: "succeed" as const };
        if (!terminated && outcome.kind === "exhaust") {
          const ci = outcome.catchIndex ?? 0;
          const cc = r.catches[ci];
          if (cc) {
            const cm = delta(mods, r, "retry", nodeId, "catch");
            runFlow(cc.body, `${nodeId}/catch:${ci}`, cm, chain);
          }
          if (!terminated && r.elseBody) {
            const em = delta(mods, r, "retry", nodeId, "elseBody");
            runFlow(r.elseBody, `${nodeId}/elseBody`, em, chain);
          }
        }
        break;
      }
      case "parallel": {
        const p = node as ParallelNode;
        const pm = delta(mods, p, "parallel", nodeId, "branch");
        // A `parallel await d { W } else { E }` whose winner P4 resolved is no
        // longer concurrent: the chosen branch is the LINEAR continuation (work
        // ran to completion, or the timer interrupted and `else` took over), so
        // it runs at the current depth — its `fail`/`finish` ends the process,
        // not just a branch (routing to the winner's continuation, design §8).
        if (p.awaitClause && p.body.form === "branches") {
          const resolved = selection.parallelOutcomes?.get(nodeId);
          if (resolved?.kind === "complete") {
            const flows: { flow: Flow; bi: number }[] = p.body.branches.map((b, bi) => ({ flow: b.body, bi }));
            flows.push({ flow: [...awaitClauseTimerFlow(p)], bi: p.body.branches.length });
            for (const bi of resolved.branches) {
              if (terminated || thrown) break;
              const entry = flows.find((x) => x.bi === bi);
              if (entry) runFlow(entry.flow, `${nodeId}/branch:${bi}`, pm, chain);
            }
            break;
          }
        }
        parallelDepth++;
        if (p.body.form === "fanOut") {
          const count = selection.loopCounts?.get(staticId(nodeId)) ?? 1;
          for (let k = 0; k < count; k++) {
            const bid = k === 0 ? `${nodeId}/fanout` : `${nodeId}/fanout#${k}`;
            runFlow(p.body.body, bid, pm, chain);
            if (terminated === "root" || thrown) break; // an injected failure truncates + propagates
            if (terminated === "branch") terminated = null; // each fan iteration is its own branch
          }
        } else {
          const baseBranches = p.body.branches;
          const branchFlows: { flow: Flow; bi: number }[] = baseBranches.map((b, bi) => ({ flow: b.body, bi }));
          // The desugared awaitClause timer/else branch sits at index
          // baseBranches.length — a `complete` outcome (P4) that wants it MUST list
          // that index, or the else/`fail "<head>"` boundary path is dropped.
          if (p.awaitClause) branchFlows.push({ flow: [...awaitClauseTimerFlow(p)], bi: baseBranches.length });
          const outcome = selection.parallelOutcomes?.get(nodeId);
          const liveIdx = outcome?.kind === "complete" ? outcome.branches : branchFlows.map((x) => x.bi);
          for (const bi of liveIdx) {
            const entry = branchFlows.find((x) => x.bi === bi);
            if (!entry) continue;
            runFlow(entry.flow, `${nodeId}/branch:${bi}`, pm, chain);
            if (terminated === "root" || thrown) break; // an injected failure truncates + propagates
            if (terminated === "branch") terminated = null; // branch-local terminator consumed at the parallel boundary
          }
          // P5 — a boolean/count merge that didn't reach threshold over the
          // pinned done-set: the completed branches ran, but the parallel can't
          // proceed, so it fails the merge (truncating the forward continuation).
          if (!terminated && !thrown && outcome?.kind === "complete" && outcome.mergeFail) {
            const reason = outcome.mergeFail.reason;
            events.push({
              kind: "terminator",
              nodeId: `${nodeId}/merge-fail`,
              type: "fail",
              ...(reason != null ? { reason } : {}),
              synthesized: true,
            });
            end = { type: "fail", ...(reason != null ? { reason } : {}) };
            terminated = "root";
          }
        }
        parallelDepth--;
        break;
      }
      case "reversible": {
        const rv = node as ReversibleNode;
        // Forward pass — run each item in order. A completed item that has an
        // `unwind` is recorded for compensation; the item a failure fires ON did
        // NOT complete, so it is never compensated (PROCESS §8).
        const completed: number[] = [];
        for (let ri = 0; ri < rv.items.length; ri++) {
          if (terminated || thrown) break;
          const it = rv.items[ri]!;
          const fwd = it.forward;
          if (fwd) {
            const m = delta(mods, rv, "reversible", nodeId, "item");
            const body = fwd.kind === "block" ? fwd.body : [fwd as ProcessNode];
            runFlow(body, `${nodeId}/item:${ri}`, m, chain);
          }
          if (thrown) break; // forward failed mid-item → this item did not complete
          if (it.unwind) completed.push(ri);
        }
        if (thrown) {
          // Fire the completed items' compensations in REVERSE fire order (the
          // saga's default outcome is `fail`, so `thrown` keeps propagating after
          // the chain — to an outer catch or the process root).
          const um = delta(mods, rv, "reversible", nodeId, "unwind");
          for (let k = completed.length - 1; k >= 0; k--) {
            const ri = completed[k]!;
            emitUnwindAction(rv.items[ri]!.unwind!.action, nodeId, `${nodeId}/unwind:${ri}`, um, chain);
            if (terminated) break; // an `unwind fail` terminated the chain
          }
        }
        break;
      }
      case "splice": {
        const sp = node as SpliceNode;
        const target = sp.target.join(".");
        const sub = subModels.get(target);
        // Unknown target / recursion → stop inlining (matches the render collapse;
        // no infinite loop). Diagnostics are surfaced by the walker's render pass.
        if (!sub || chain.includes(target)) break;
        const substituted =
          sub.templateParam && sp.templateCaller?.form === "explicit" && sp.templateCaller.resolved
            ? substituteTemplateCallerIR(sub.body, sub.templateParam.name, sp.templateCaller.resolved)
            : sub.body;
        const body = demoteMultiAwaitHead(substituted ?? []);
        const m = delta(mods, sp, "splice", nodeId, "body", { expanded: true });
        runFlow(body, `${nodeId}/body`, m, [...chain, target]);
        break;
      }
      case "owner_block":
        runFlow((node as OwnerBlockNode).body, `${nodeId}/body`, mods, chain);
        break;
      case "description":
        break;
      default:
        break;
    }
  }

  runFlow(demoteMultiAwaitHead(process.body ?? []), rootId, DEFAULT_MODS, []);

  // P3 — a failure that reached the process root with no handler is the process's
  // fail outcome (forward continuation already pruned); carry the injected cause.
  // (`thrown` is mutated only in nested closures, so CFA cannot see it non-null
  // here — read it through an explicitly-typed alias.)
  const rootThrown = thrown as { readonly cause?: string } | null;
  if (rootThrown && !end) {
    const cause = rootThrown.cause;
    events.push({
      kind: "terminator",
      nodeId: `${rootId}/inject-fail`,
      type: "fail",
      ...(cause != null ? { reason: cause } : {}),
      synthesized: true,
    });
    end = { type: "fail", ...(cause != null ? { reason: cause } : {}) };
  }

  resolveReentries(steps, reentries);

  return { events, steps, stepNodes, ...(end ? { end } : {}), open };
}

/** The catch index whose label matches an injected failure's cause (PROCESS §4).
 *  An exact label match wins; else the first bare (label-less / catch-all) catch;
 *  else -1 (no handler → the failure propagates). */
function catchIndexForCause(catches: readonly CatchClause[], cause: string | undefined): number {
  const exact = catches.findIndex((c) => c.label?.text === cause);
  if (exact >= 0) return exact;
  return catches.findIndex((c) => c.label == null || c.label.text === "");
}

/** A retry's numeric attempt bound (`each 3 try` → 3). A non-numeric head
 *  (collection / phrase) has no static count — fall back to a modest cap so an
 *  injected all-fail sequence still terminates into `else`. */
function retryBound(r: RetryNode): number {
  const label = r.bound?.label?.text?.trim();
  if (label && /^\d+$/.test(label)) return Number.parseInt(label, 10);
  return 8;
}
